import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { paths } from '@/constants/paths'
import { runExperimentsOnServer } from '@/experimentation/server'
import { Seo } from '@/layout/Seo'
import { getContentfulClient } from '@/services/ApolloClient'
import { getSweepstakes } from '@/services/SweepstakeService/SweepstakeService'
import { getAllUserGuildDiscounts } from '@/services/Theatrical/queries'
import { GetTheatricalLandingPageOldQuery, UltrawideBanner } from '@/types/codegen-contentful'
import { SweepstakeObject, TheatricalDiscount, TheatricalReleaseObject } from '@/types/codegen-federation'
import DatadogRumFull from '@/utils/datadog/DatadogRumFull'
import { getServerSidePropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { isDefined } from '@/utils/types'
import { getUserGuildStatus } from '@/utils/users/usersServer'
import { SimpleTicketItem } from '@/views/TicketCheckoutViews/hooks/useSimpleTicketList'
import { GET_THEATRICAL_LANDING_PAGE_OLD, TicketsLandingViewOld } from '@/views/TicketsLandingView'
import { NewTicketsLandingView } from '@/views/TicketsLandingView/NewTicketsLandingView'
import { comingSoonTheatricalReleases } from '@/views/TicketsLandingView/NewTicketsLandingView/comingSoonTheatrical'
import {
  getNewTheatricalLandingReleases,
  getUserTickets,
} from '@/views/TicketsLandingView/NewTicketsLandingView/queries'
import {
  ComingSoonTheatricalRelease,
  CurrentTheatricalRelease,
  PastTheatricalRelease,
} from '@/views/TicketsLandingView/NewTicketsLandingView/types'
import {
  bySweepstakeEndDateAsc,
  byTheatricalReleaseDateAsc,
  byTheatricalReleaseDateDesc,
  isValidCurrentTheatricalRelease,
  isValidPastTheatricalRelease,
  mapToCurrentTheatricalRelease,
  mapToPastTheatricalRelease,
} from '@/views/TicketsLandingView/NewTicketsLandingView/utils'
import { byReleaseDateAsc, byReleaseDateDesc, mapToTicketsLandingRelease } from '@/views/TicketsLandingView/transforms'
import { TicketsLandingRelease } from '@/views/TicketsLandingView/types'

interface TicketsLandingPageProps {
  oldPage: OldPageProps
  newPage: NewPageProps
  locale: string
  showNewDesign?: boolean
  showHeroExperiment?: boolean
}

export function TicketsLandingPage(props: TicketsLandingPageProps) {
  const { t } = useTranslate('tickets')
  const basePath = getBasePath(props.locale)
  const url = `${basePath}${paths.tickets.index}`
  const description = t(
    'ticketsLandingPageDescription',
    "Buy tickets and get showtimes for Angel Originals at a theater near you today. See what's currently in theaters or explore upcoming and past releases. Angel Guild members get 2 tickets for every theatrical release as part of their membership.",
  )
  const title = t('ticketLandingPageTitle', 'Tickets & Showtimes | In Theaters | Angel Studios')

  const openGraph = {
    url,
    description,
    title,
  }

  return (
    <>
      <DatadogRumFull />
      <Seo canonical={url} description={description} openGraph={openGraph} path={paths.tickets.index} title={title} />
      {props.showNewDesign ? (
        <NewTicketsLandingView {...props.newPage} showHeroExperiment={props.showHeroExperiment} />
      ) : (
        <TicketsLandingViewOld {...props.oldPage} />
      )}
    </>
  )
}

export const getServerSideProps = getServerSidePropsErrorHandler(
  async ({ locale, preview, federationClient, userId, req, res }) => {
    const [oldPage, newPage, experiments] = await Promise.all([
      fetchOldPageProps(preview),
      fetchNewPageProps(federationClient, userId),
      runExperimentsOnServer(req, res, [
        { experimentKey: 'tickets-landing-new-design-v2', defaultValue: false },
        { experimentKey: 'tickets-hero-image2', defaultValue: false },
      ]),
    ])

    const showHeroExperiment = experiments['tickets-hero-image2']
    const showNewDesign = experiments['tickets-landing-new-design-v2']

    return {
      props: {
        locale,
        oldPage,
        newPage,
        showNewDesign,
        showHeroExperiment,
        ...(await loadTranslations(locale, ['common', 'tickets', 'account', 'home'])),
      },
    }
  },
)

interface OldPageProps {
  upcoming: TicketsLandingRelease[]
  inTheaters: TicketsLandingRelease[]
  pastReleases: TicketsLandingRelease[]
  banner?: UltrawideBanner
}
async function fetchOldPageProps(preview: boolean): Promise<OldPageProps> {
  const { data } = await getContentfulClient({
    preview,
  }).query<GetTheatricalLandingPageOldQuery>({
    query: GET_THEATRICAL_LANDING_PAGE_OLD,
    variables: {
      preview,
    },
  })

  const upcoming = data?.upcoming?.items?.map(mapToTicketsLandingRelease).filter(isDefined).sort(byReleaseDateAsc) || []
  const inTheaters =
    data?.inTheaters?.items?.map(mapToTicketsLandingRelease).filter(isDefined).sort(byReleaseDateAsc) || []
  const pastReleases =
    data?.pastReleases?.items?.map(mapToTicketsLandingRelease).filter(isDefined).sort(byReleaseDateDesc) || []
  const banner = (data?.banners?.items?.[0] as UltrawideBanner) ?? undefined

  return {
    upcoming,
    inTheaters,
    pastReleases,
    banner: banner,
  }
}

interface NewPageProps {
  currentReleases: CurrentTheatricalRelease[]
  comingSoonReleases: ComingSoonTheatricalRelease[]
  pastReleases: PastTheatricalRelease[]
  userTickets: SimpleTicketItem[]
  sweepstakes: SweepstakeObject[]
  isBasicGuildUser: boolean
  isPremiumGuildUser: boolean
  isLoggedIn: boolean
}

const fetchNewPageProps = async (
  federatedClient: ApolloClient<NormalizedCacheObject>,
  userId: string | null,
): Promise<NewPageProps> => {
  const [releases, guildStatus, userTickets, sweepstakes, guildDiscounts] = await Promise.all([
    getNewTheatricalLandingReleases({ client: federatedClient }),
    getUserGuildStatus(userId),
    getUserTickets({ client: federatedClient, userId }),
    getSweepstakes(federatedClient, ['LIVE']),
    getAllUserGuildDiscounts(federatedClient, userId),
  ])

  const isPremiumGuildUser = !!guildStatus?.isGuildMember && !!guildStatus?.guildRoles?.hasFreeTickets
  const isBasicGuildUser = !!guildStatus?.isGuildMember && !guildStatus?.guildRoles?.hasFreeTickets

  const sortedSweepstakes = sweepstakes.sort(bySweepstakeEndDateAsc)

  const currentReleases = extractCurrentReleases({
    releases: releases ?? [],
    sweepstakes: sweepstakes ?? [],
    guildDiscounts: isPremiumGuildUser ? guildDiscounts : [],
  })
  const pastReleases = extractPastReleases(releases ?? [])

  return {
    currentReleases,
    comingSoonReleases: comingSoonTheatricalReleases,
    pastReleases,
    isBasicGuildUser,
    isPremiumGuildUser,
    isLoggedIn: !!userId,
    userTickets,
    sweepstakes: sortedSweepstakes,
  }
}

const extractCurrentReleases = ({
  releases,
  guildDiscounts,
  sweepstakes,
}: {
  releases: TheatricalReleaseObject[]
  guildDiscounts?: TheatricalDiscount[]
  sweepstakes?: SweepstakeObject[]
}): CurrentTheatricalRelease[] => {
  return releases
    .filter((release) => release?.region?.ticketsStatus !== 'ENDED')
    .map(mapToCurrentTheatricalRelease)
    .filter(isValidCurrentTheatricalRelease)
    .map((release) => addGuildCodesToRelease(release, guildDiscounts))
    .map((release) => addSweepstakeBooleanToRelease(release, sweepstakes))
    .sort(byTheatricalReleaseDateAsc)
}

const extractPastReleases = (releases: TheatricalReleaseObject[]): PastTheatricalRelease[] => {
  return releases
    .filter((release) => release?.region?.ticketsStatus === 'ENDED')
    .map(mapToPastTheatricalRelease)
    .filter(isValidPastTheatricalRelease)
    .sort(byTheatricalReleaseDateDesc)
}

const addSweepstakeBooleanToRelease = (
  release: CurrentTheatricalRelease,
  sweepstakes: SweepstakeObject[] | undefined,
): CurrentTheatricalRelease => {
  const activeSweepstake = !!sweepstakes?.some((sweepstake) => sweepstake?.slug === release.theatricalSlug)

  return { ...release, activeSweepstake }
}

const addGuildCodesToRelease = (
  release: CurrentTheatricalRelease,
  guildDiscounts: TheatricalDiscount[] | undefined,
): CurrentTheatricalRelease => {
  const guildCodes = guildDiscounts
    ?.filter((discount) => discount?.theatricalSlug === release.theatricalSlug)
    ?.map((discount) => discount?.code)

  return { ...release, guildCodes }
}

export default TicketsLandingPage
